import React, { useMemo } from 'react';

import { CUSTOM_WORKFLOW_TYPE_LABEL } from '@aline/common';
import { Typography } from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { FilePen, LayoutTemplate } from 'lucide-react';
import type { WorkflowTypeFilterOption } from 'src/components/workflow/workflow-list/workflow-type';
import { Apollo } from 'src/gql/generated';

import { FilterPopoverContent } from './FilterPopoverContent';
import type { FilterContentProps } from './types';

const CUSTOM_OPTION_ID = 'custom';
const CUSTOM_OPTION = {
  __typename: 'CustomWorkflowType',
  id: CUSTOM_OPTION_ID,
  label: CUSTOM_WORKFLOW_TYPE_LABEL,
} as const;

export function WorkflowTypeFilterContent({ field, operator }: FilterContentProps): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const { data } = Apollo.useGetWorkflowTypesQuery();

  const options = useMemo(
    (): WorkflowTypeFilterOption[] => [CUSTOM_OPTION, ...(data?.workflowTypes ?? [])],
    [data?.workflowTypes]
  );

  const selectedIds = useMemo(() => {
    const filterItem = filterModel.items.find(item => item.field === field);
    const rawValues = filterItem?.value ?? [];
    if (!Array.isArray(rawValues)) return new Set<string>();
    return new Set(rawValues.map((value: WorkflowTypeFilterOption) => value.id));
  }, [filterModel, field]);

  const handleToggle = (optionId: string) => {
    const currentValues: WorkflowTypeFilterOption[] =
      filterModel.items.find(item => item.field === field)?.value ?? [];
    const isSelected = selectedIds.has(optionId);
    const newValues = isSelected
      ? currentValues.filter(value => value.id !== optionId)
      : [...currentValues, ...options.filter(option => option.id === optionId)];

    const updatedItems = filterModel.items.filter(item => item.field !== field);
    if (newValues.length > 0) updatedItems.push({ field, operator, value: newValues });

    apiRef.current.setFilterModel({ ...filterModel, items: updatedItems });
  };

  return (
    <FilterPopoverContent
      options={options}
      renderOption={option => {
        const Icon = option.id === CUSTOM_OPTION_ID ? FilePen : LayoutTemplate;
        return (
          <>
            <Icon size={16} />
            <Typography variant="body2">{option.label}</Typography>
          </>
        );
      }}
      selectedIds={selectedIds}
      onToggle={handleToggle}
    />
  );
}
